import { cloneDeep, debounce, isEqual } from 'lodash'

import { globalErrorHandler } from '@/utils'

import { requestItem, requestItems } from '../cache'

/**
 * Локальное состояние загрузки элементов из сервиса.
 *
 * @typedef {object} ServiceState
 * @property {Record<string, any>} _initQuery Условия отбора, с которыми
 * был выполнен последний запрос.
 * @property {Function} _debouncedFindAll Отложенный перезапрос элементов
 * с задержкой 200 миллисекунд.
 */

/**
 * Загрузка элементов выпадающего списка из сервиса: параметры
 * запроса и перезапрос при смене сервиса, строки поиска или
 * условий отбора.
 */
export const service = {
  props: {
    /**
     * Имя сервиса, из которого загружаются элементы. Если не
     * задано, используются элементы свойства items, а отбор по
     * строке поиска выполняется на стороне компонента.
     *
     * @type {string}
     */
    service: String,

    /**
     * Дополнительные условия отбора элементов. Добавляются к
     * строке запроса; их изменение вызывает перезапрос элементов.
     *
     * @type {Record<string, any>}
     * @default {}
     */
    query: {
      type: Object,
      default: () => {
        return {}
      }
    },

    /**
     * Запрос списка элементов. Вызывается в контексте компонента и
     * отдает массив элементов. По умолчанию элементы
     * запрашиваются у сервиса постранично с учетом строки поиска.
     *
     * @type {Function}
     */
    requestItems: {
      type: Function,
      default: requestItems
    },
    /**
     * Запрос одного элемента по идентификатору. Вызывается в
     * контексте компонента при догрузке выбранных элементов. По
     * умолчанию элемент запрашивается у сервиса.
     *
     * @type {Function}
     */
    requestItem: {
      type: Function,
      default: requestItem
    },

    /**
     * Преобразование полученного списка элементов перед выводом.
     * Если не задано, список выводится без изменений.
     *
     * @type {Function}
     * @default undefined
     */
    clearItems: {
      type: Function,
      default: undefined
    },

    /**
     * Поколение сервиса для формирования строки запроса. Если не
     * задано, поколение определяется по имени сервиса.
     *
     * @type {string}
     */
    backendGeneration: String
  },

  /**
   * Локальное состояние загрузки элементов из сервиса.
   *
   * @returns {ServiceState} Локальное состояние.
   */
  data() {
    return {
      _initQuery: cloneDeep(this.query),
      _debouncedFindAll: debounce(
        function() {
          this.findAll()
        },
        200,
        {
          leading: false,
          trailing: true
        }
      )
    }
  },

  watch: {
    /**
     * Отслеживает смену сервиса и планирует перезапрос элементов.
     */
    service() {
      this._scheduleFindAll()
    },

    /**
     * Отслеживает изменения строки поиска и планирует перезапрос
     * элементов.
     */
    search() {
      this._scheduleFindAll()
    },

    /**
     * Отслеживает изменения условий отбора с глубоким сравнением.
     * Если новые условия отличаются от условий последнего запроса,
     * планируется перезапрос элементов и новые условия
     * запоминаются. Иначе ничего не происходит.
     */
    query: {
      handler(value) {
        if (!isEqual(value, this._data._initQuery)) {
          this._scheduleFindAll()
          this._data._initQuery = cloneDeep(this.query)
        }
      },
      deep: true
    }
  },

  methods: {
    /**
     * Планирует перезапрос элементов.
     * Если задано свойство service, сразу включается признак
     * загрузки, чтобы поле не подсвечивалось ошибкой
     * обязательного выбора на время задержки перед запросом.
     */
    _scheduleFindAll() {
      if (this.service) {
        this._data._loading = true
      }
      this._data._debouncedFindAll.call(this)
    },

    /**
     * Загружает элементы из сервиса.
     *
     * Если свойство service не задано, ничего не происходит.
     *
     * Перед запросом запоминаются условия отбора и строка поиска,
     * включается признак загрузки. Если к моменту ответа
     * условия или строка поиска изменились, ответ считается
     * устаревшим и не попадает ни в список элементов, ни в событие
     * fetched; актуальные данные приносит перезапрос,
     * запланированный наблюдателями за query и search.
     *
     * Если ответ получен, наружу отправляется событие fetched с
     * полученными элементами. Если задано свойство clearItems,
     * элементы перед выводом проходят через него, иначе выводятся
     * без изменений.
     *
     * Если запрос завершился ошибкой, список элементов не меняется,
     * а ошибка передается в globalErrorHandler.
     *
     * Признак загрузки снимается после актуального ответа и после
     * ошибки. На устаревшем ответе признак остается включенным,
     * потому что запланирован перезапрос, и поле не должно
     * подсвечиваться ошибкой обязательного выбора между запросами.
     */
    async findAll() {
      if (this.service) {
        const requestQuery = cloneDeep(this.query)
        const requestSearch = this.search
        let isStaleResponse = false
        try {
          this._data._loading = true
          const result = await this.requestItems()
          isStaleResponse = !isEqual(requestQuery, this.query) || requestSearch !== this.search
          if (isStaleResponse) {
            return
          }
          if (result) {
            this.$emit('fetched', result)
            if (this.clearItems && typeof this.clearItems === 'function') {
              this._data._items = await this.clearItems(result)
            } else {
              this._data._items = result
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          if (!isStaleResponse) {
            this._data._loading = false
          }
        }
      }
    }
  }
}
